/**
 * Spec 027 US 4 — TeamNamePending wait screen.
 *
 * V2 rebuild against Figma frame a_start_team_name_pending (393×884).
 * Functional contract (WS subscribe → polling fallback → timeout banner,
 * advance via team.name_updated) is preserved verbatim from V1.
 */
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { getTeam } from '../../api/teams';
import OnboardingShellV2 from '../../components/OnboardingShellV2';
import ProgressIndicatorV2 from '../../components/ProgressIndicatorV2';
import heroPending from '../../assets/onboarding/hero-team-pending.svg';
import infoIcon from '../../assets/onboarding/info.svg';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useWizardState } from '../../wizard/useWizardState';
import { getStepInfo } from '../../wizard/wizardSteps';
import { pillButtonFocusRing } from '../../styles/onboardingButtons';

const WS_SILENCE_TO_POLLING_MS = 15_000;
const POLLING_INTERVAL_MS = 5_000;
const TOTAL_TIMEOUT_MS = 30_000;

const TeamNamePendingStep: React.FC = () => {
  const { t } = useTranslation('onboarding');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const wizard = useWizardState();
  const tf = wizard.teamFlow;

  const [showConnectionLost, setShowConnectionLost] = useState(false);
  const [members, setMembers] = useState<Array<{ id: string; display_name: string }>>([]);
  const lastWsActivityRef = useRef<number | null>(null);
  const pollingTimerRef = useRef<number | null>(null);
  const totalTimeoutRef = useRef<number | null>(null);

  const step = getStepInfo('team-name-pending', 'with-code');
  const canQuery = !!wizard.organizationSlug && !!tf.runId && !!tf.teamId;

  const advance = useCallback(
    (newName: string) => {
      wizard.patchTeamFlow({ teamNameFromServer: newName });
      if (tf.runId) {
        if (tf.teamId) {
          try {
            sessionStorage.setItem(`clavis_team_for_run_${tf.runId}`, tf.teamId);
          } catch { /* sessionStorage disabled */ }
        }
        navigate(`/runs/${tf.runId}/participant`);
      }
    },
    [navigate, tf.runId, tf.teamId, wizard],
  );

  useWebSocket({
    path:
      canQuery
        ? `/api/orgs/${wizard.organizationSlug}/runs/${tf.runId}/ws`
        : '',
    enabled: canQuery,
    handlers: {
      'team.name_updated': (data) => {
        lastWsActivityRef.current = Date.now();
        const payload = data as { team_id: string; name: string | null } | undefined;
        if (!payload) return;
        if (payload.team_id !== tf.teamId) return;
        if (payload.name) advance(payload.name);
      },
    },
  });

  useEffect(() => {
    if (!canQuery) return;
    if (lastWsActivityRef.current === null) lastWsActivityRef.current = Date.now();

    const startPolling = () => {
      if (pollingTimerRef.current !== null) return;
      pollingTimerRef.current = window.setInterval(async () => {
        try {
          const team = await getTeam({
            orgSlug: wizard.organizationSlug!,
            runId: tf.runId!,
            teamId: tf.teamId!,
          });
          if (team.members) setMembers(team.members);
          if (team.name) {
            advance(team.name);
          }
        } catch {
          // swallow — banner timer will handle terminal state
        }
      }, POLLING_INTERVAL_MS);
    };

    const silenceCheck = window.setInterval(() => {
      const lastActivity = lastWsActivityRef.current ?? Date.now();
      if (Date.now() - lastActivity > WS_SILENCE_TO_POLLING_MS) {
        startPolling();
      }
    }, 1_000);

    totalTimeoutRef.current = window.setTimeout(() => {
      setShowConnectionLost(true);
    }, TOTAL_TIMEOUT_MS);

    return () => {
      window.clearInterval(silenceCheck);
      if (pollingTimerRef.current !== null) {
        window.clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      if (totalTimeoutRef.current !== null) {
        window.clearTimeout(totalTimeoutRef.current);
        totalTimeoutRef.current = null;
      }
    };
  }, [canQuery, advance, tf.runId, tf.teamId, wizard.organizationSlug]);

  const memberList =
    members.length === 0
      ? wizard.displayName || '...'
      : members.map((m) => m.display_name).join(', ');

  return (
    <OnboardingShellV2
      progress={
        <ProgressIndicatorV2
          current={step.current}
          total={step.total}
        />
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingBlockStart: '66px',
          gap: '24px',
        }}
      >
        <Box
          component="img"
          src={heroPending}
          alt=""
          sx={{
            width: '93px',
            height: '86px',
            alignSelf: 'center',
            display: 'block',
          }}
        />
        <Typography
          component="h1"
          sx={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '32px',
            letterSpacing: 0,
            color: '#322F35',
            margin: 0,
            // headline/small half-leading: glyph sits 2 px below the line-box top.
            marginBlockStart: '-2px',
            // bbox-height drift (skill §6 4th-drift): CSS lh=32 vs Figma bbox
            // h=28 → CSS box 4 px taller. Pull -3 at the bottom to neutralize
            // the propagation to all downstream siblings.
            marginBlockEnd: '-3px',
          }}
        >
          {t('pending.title')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              color: '#322F35',
              // body/large half-leading (figma-implement skill §6 3rd-drift):
              // CSS line-box has 3 px above the glyph, Figma's text-bbox is
              // tight to the glyph. Pull box up so glyph lands at Figma's y.
              marginBlockStart: '-3px',
            }}
          >
            {t('pending.membersHeading')}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: '#322F35',
              // bbox-height drift (figma-implement skill §6 4th-drift):
              // Figma members-value bbox h=23 (body/medium), CSS lh=20 →
              // CSS box 3 px shorter. Extend block bottom so info-box
              // sibling lands at Figma's y=305.
              paddingBlockEnd: '3px',
            }}
          >
            {memberList}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: '#E6E9F4',
            borderRadius: '22px',
            // Figma symmetric paddings 13/14/13/14. The 14 px of slack that
            // make the box total h=107 (vs CSS body 40 = h=94) live INSIDE
            // Figma's body-bbox h=54 (Figma renders 14 px of empty space
            // below the last glyph, inside the bbox). Apply that slack as
            // body-Typography paddingBlockEnd, NOT as asymmetric box padding,
            // so the body element visually extends close to the box bottom
            // (matches Figma's "body fills the box" optical balance).
            paddingBlockStart: '13px',
            paddingBlockEnd: '13px',
            // Hug-Overflow fix (figma-implement skill §6 5th-drift): Figma's
            // Body Text overflows the container's paddingRight=14 by 5 px to
            // the right (effective text-right margin = 9). Match it in CSS so
            // the body wraps the same way Figma renders it.
            paddingLeft: '14px',
            paddingRight: '9px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <Box
            sx={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#4D69B9',
              color: '#FFFFFF',
              borderRadius: '8px',
              paddingInline: '8px',
              paddingBlock: '1px',
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '11px',
              fontWeight: 500,
              lineHeight: '16px',
              letterSpacing: '0.5px',
            }}
          >
            <Box
              component="img"
              src={infoIcon}
              alt=""
              aria-hidden
              sx={{
                width: '12px',
                height: '12px',
                display: 'block',
                // info.svg paths are #1D1B20 — flip to white to match Figma chip fill.
                filter: 'brightness(0) invert(1)',
              }}
            />
            {tc('infoBadge')}
          </Box>
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: '#00348D',
              // Figma body bbox h=54 vs CSS lh=20 × 2 lines = 40 → +14 px of
              // descender slack INSIDE the body bbox (Figma renders empty
              // below last glyph, inside the body element). Put that slack
              // here (not on the box) so the body visually extends close to
              // the info-box bottom — matches Figma's "body fills box"
              // optical balance, not "body top-weighted with empty bottom".
              paddingBlockEnd: '14px',
            }}
          >
            {t('pending.info')}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0.5px',
            color: '#322F35',
            // Upstream cascade is balanced (title mb-end-3 + info-box pad-end
            // 26 + members-block half-leading). Discussion natural box top
            // lands at Figma-bottom-of-info-box +24 ≈ Figma's discussion-y,
            // so CSS glyph (with half-leading +3) lands within 1 px of
            // Figma's bbox-top glyph. No own mb needed.
          }}
        >
          {t('pending.discussionPrompt')}
        </Typography>
        <Box
          role="status"
          aria-live="polite"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          {/* M3 Loading Indicator from @material/web (registered in main.tsx).
              Sized + colored via CSS custom properties to match Figma's
              Circular_loading indicator: 44×44, brand #4D69B9, 8-dp thickness
              (≈ 18% of size). React 19 handles the custom element natively. */}
          <md-circular-progress
            indeterminate
            aria-label={t('pending.spinnerLabel')}
            style={{
              ['--md-circular-progress-size' as string]: '44px',
              ['--md-circular-progress-active-indicator-color' as string]: '#4D69B9',
              ['--md-circular-progress-active-indicator-width' as string]: '18',
            } as React.CSSProperties}
          />
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              color: '#1D47A4',
            }}
          >
            {t('pending.spinnerLabel')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
            // Figma Frame 34 is 48 tall with 40-tall pills centered (M3 state-layer band).
            alignItems: 'center',
            minHeight: '48px',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => navigate(-1)}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: 'pointer',
              minHeight: '40px',
              paddingInline: '16px',
              borderRadius: '100px',
              // Figma Back stroke=#CAC4D0@1 strokeAlign=INSIDE — inset shadow
              // keeps the pill at 40 width inside the bbox; CSS border would
              // add 2 px to the rendered width.
              border: 'none',
              boxShadow: 'inset 0 0 0 1px #CAC4D0',
              color: '#49454F',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: 'rgba(73, 69, 79, 0.04)' },
              ...pillButtonFocusRing,
            }}
          >
            {t('pending.back')}
          </Box>
          <Box
            component="button"
            type="button"
            disabled
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: 'not-allowed',
              minHeight: '40px',
              paddingInline: '16px',
              borderRadius: '100px',
              // M3 disabled container: on-surface @ 12% (rgba(29,27,32,0.12)).
              backgroundColor: 'rgba(29, 27, 32, 0.12)',
              // M3 disabled label: on-surface @ 38%.
              color: 'rgba(29, 27, 32, 0.38)',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {t('pending.submit')}
          </Box>
        </Box>
        {showConnectionLost && (
          <Typography
            role="alert"
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              color: '#8A4A00',
              backgroundColor: '#FFF4E5',
              border: '1px solid #F0B27A',
              borderRadius: '8px',
              padding: '12px',
            }}
          >
            {t('pending.connectionLost')}
          </Typography>
        )}
      </Box>
    </OnboardingShellV2>
  );
};

export default TeamNamePendingStep;
